import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MouseEvent } from 'react';
import { Autocomplete, Box, Button, Checkbox, Chip, Dialog, MenuItem, Select, TextField, styled } from '@mui/material';
import Progress from '../../progress/Progress';
import { EFieldType, EValueType, IField, EFieldTypeKey, EValueTypeKey, IFieldOption } from '../../../models/form/form';
import objectUtils from '../../../utils/objectUtils';
import ModalCard, { ModalTitle } from '../../modal/ModalCard';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250
        }
    }
};

const ModalWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'stretch',
    flex: 1
}));

const EntryRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flex: 1,
    marginBottom: '1em'
}));

const Title = styled(ModalTitle)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flex: 1
}));

const EntryField = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flex: 2,
    width: '100%',
    marginLeft: '.5em'
}));

interface IEditFieldModalProps {
    isShowEdit: boolean;
    setIsShowEdit: (v: boolean) => void;
    editField: (field: IField) => void;
    fieldToEdit: IField;
}

const EditFieldModal = ({ isShowEdit, setIsShowEdit, editField, fieldToEdit }: IEditFieldModalProps) => {
    const [text, setText] = useState<string>(fieldToEdit.text);
    const [options, setOptions] = useState<IFieldOption[]>(fieldToEdit.options);
    const [isRequired, setIsRequired] = useState<boolean>(fieldToEdit.required);
    const [isMultiselect, setIsMultiSelect] = useState<boolean>(fieldToEdit.multiselect);
    const [fieldType, setFieldType] = useState<EFieldType>(fieldToEdit.fieldType);
    const [valueType, setValueType] = useState<EValueType>(fieldToEdit.valueType);
    const [isFormSending, setIsFormSending] = useState(false);

    const { t } = useTranslation(['form', 'common']);

    const onEditField = (e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        setIsFormSending(true);

        const field: IField = {
            id: fieldToEdit.id,
            fieldType,
            options,
            text,
            valueType,
            multiselect: isMultiselect,
            required: isRequired,
            position: fieldToEdit.position,
            values: [],
            isTranslated: false
        };
        editField(field);
        closeModal();
    };

    const closeModal = () => {
        setIsShowEdit(false);
        setIsFormSending(false);
    };

    return (
        <Dialog open={isShowEdit} onClose={closeModal}>
            <ModalCard sx={{ borderRadius: 4, height: '100%', overflow: 'auto' }} minWidth={550} title={t('form:field.editField')} closeCallback={closeModal}>
                <form>
                    <ModalWrapper>
                        <EntryRow>
                            <Title>{t('form:field.label')}</Title>
                            <EntryField>
                                <TextField
                                    required={true}
                                    style={{ textAlign: 'left', width: '100%' }}
                                    value={text}
                                    onChange={(e) => {
                                        setText(e.target.value);
                                    }}
                                />
                            </EntryField>
                        </EntryRow>
                        <EntryRow style={{ display: fieldType === EFieldType.Select ? '' : 'none' }}>
                            <Title>{t('form:field.options')}</Title>
                            <EntryField>
                                <Autocomplete
                                    sx={{ width: '100%' }}
                                    multiple
                                    freeSolo
                                    options={options.map((option) => option.value)}
                                    defaultValue={options.map((option) => option.value)}
                                    onChange={(_, value) => {
                                        const options = (value as string[]).map((v, i) => (typeof v === 'string' ? { id: i, value: v } : v));
                                        setOptions(options);
                                    }}
                                    renderInput={(params) => <TextField
                                        {...params}
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{ ...params.InputProps }}
                                    />}
                                />
                            </EntryField>
                        </EntryRow>
                        <EntryRow style={{ display: fieldType === EFieldType.Select ? '' : 'none' }}>
                            <Title>{t('form:field.multiSelect')}</Title>
                            <EntryField>
                                <Checkbox
                                    defaultChecked={isMultiselect}
                                    onChange={(e) => {
                                        setIsMultiSelect(!isMultiselect);
                                    }}
                                />
                            </EntryField>
                        </EntryRow>
                        <EntryRow style={{ display: fieldType !== EFieldType.Text ? '' : 'none' }}>
                            <Title>{t('form:field.required')}</Title>
                            <EntryField>
                                <Checkbox
                                    defaultChecked={isRequired}
                                    onChange={() => {
                                        setIsRequired(!isRequired);
                                    }}
                                />
                            </EntryField>
                        </EntryRow>
                    </ModalWrapper>
                    <ModalCard.Action>
                        <Progress isLoading={isFormSending}>
                            <Button type="submit" variant="contained" color="primary" onClick={onEditField}>
                                <ModalTitle>{t('form:field.edit')}</ModalTitle>
                            </Button>
                        </Progress>
                    </ModalCard.Action>
                </form>
            </ModalCard>
        </Dialog>
    );
};

export default EditFieldModal;
